/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import PropTypes from 'prop-types';
import { FlexGrid, Text, TextArea } from '@gasbuddy/react-components';
import Loader from '@gasbuddy/react-components/build/components/Loader';
import Modal from '../../modal';
import { StationReviewType } from '../../../utils/prop-types/review-types';
import Notification from '../../notification';
import ReplyTemplateSelector from '../reply-template-selector';
import sharedStyles from '../../../styles/shared.css';
import styles from './styles.css';
import HowToRespondButton from '../how-to-respond-button';
import Button from '../../button';

const { api, logError, getStationBrand } = require('../../../../lib/bputils').default;
const { canReplyWithFreeFormText } = require('../../../../lib/permissions').default;

const canReplyFreeForm = canReplyWithFreeFormText();

export default function ReplyReview({ review, onReplyCreated, className }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [replyText, setReplyText] = React.useState('');

  const handleOnChangeReplyText = (e) => {
    const { value } = e.currentTarget;
    setReplyText(value);
  };

  const onClickSubmitReplyButton = async () => {
    setIsLoading(true);

    try {
      const response = await api.post({
        path: `/reviews/${review.id}/reply`,
        data: {
          text: replyText,
        },
      });

      onReplyCreated(response.reply);

      Notification.success('Reply was submitted successfully');
      setIsLoading(false);
    } catch (err) {
      logError(err);
      if (err.status === 409) {
        Notification.error('Cannot reply to review, there is no brand associated with this station.');
      } else {
        Notification.error('There was an error replying to this review. Please contact support.');
      }
      setIsLoading(false);
    }
  };

  const handleOnChangeReplyTemplateSelector = (selectedTemplate) => {
    if (selectedTemplate) {
      setReplyText(selectedTemplate.message);
    }
  };

  const getSubmitReplyButton = () => (
    <Modal
      title="Are you sure you want to submit this reply?"
      className={className}
      saveButtonText="SUBMIT REPLY"
      onSave={onClickSubmitReplyButton}
      button={(
        <Button
          className={styles.submitReplyButton}
          tertiary
          onClick={onClickSubmitReplyButton}
          disabled={!replyText.length}
        >
          SUBMIT REPLY
        </Button>
      )}
      gTagTitle="Reply A Review Popup"
    >
      <Text className={styles.secondaryText} centered>
          You cannot undo a reply, so make sure this is exactly how you would like to respond to your customer.
          Once you reply, it may take a little while to show up, but it will be below the review you are replying to.
      </Text>
    </Modal>
  );

  return (
    isLoading ? <Loader /> : (
      <FlexGrid className={styles.replyReviewContainer} container>
        {/* Row 1 */}
        <FlexGrid.Column desktop={5} mobile={12} tablet={6} className={`${styles.paddingTop}`}>
          <div className={styles.marginLeft}>
            <ReplyTemplateSelector
              onChange={handleOnChangeReplyTemplateSelector}
              enableTemplateEditor={canReplyFreeForm}
            />
          </div>
        </FlexGrid.Column>

        <FlexGrid.Column desktop={7} mobile={12} tablet={6} className={styles.helpText}>
          <div>
            <div className={`${styles.text} ${styles.bold}`}>
            Not sure how to respond to customer reviews?
            </div>
            <div className={`${styles.text}`}>
            We’ve got some helpful tips to get you get started.
            </div>
          </div>
          <div>
            <HowToRespondButton />
          </div>
        </FlexGrid.Column>

        {/* Row 2 */}
        <FlexGrid.Column desktop={12} mobile={12} tablet={12} className={`${styles.row2} ${styles.paddingTop}`}>
          <img
            src={getStationBrand(review.station.brandings).brand_logo}
            alt=""
          />
          <TextArea
            size="sm"
            onChange={handleOnChangeReplyText}
            value={replyText}
            disabled={!canReplyFreeForm}
            placeholder="Type your reply here or select a reply template above"
          />
        </FlexGrid.Column>

        {/* Row 3 */}
        <FlexGrid.Column desktop={8} mobile={12} tablet={12} className={`${styles.row3}`}>
          <div className={styles.marginLeft}>
            <Text className={sharedStyles.secondaryText}>
            Please keep in mind that this reply will be visible in the GasBuddy app and on our website.
            You are submitting this reply on behalf of the station & fuel brand so please review before you submit.
            </Text>
          </div>
        </FlexGrid.Column>
        <FlexGrid.Column desktop={4} mobile={12} tablet={12} className={styles.row3}>
          <div>
            {getSubmitReplyButton()}
          </div>
        </FlexGrid.Column>
      </FlexGrid>
    )
  );
}

ReplyReview.propTypes = {
  review: StationReviewType.isRequired,
  onReplyCreated: PropTypes.func.isRequired,
  className: PropTypes.string,
};

ReplyReview.defaultProps = {
  className: '',
};
